import React, { useEffect, useRef, useState } from "react";
import { Settings, Bell, Mail, Wrench, Coffee, MessageCircle, Users, Trash2, LogOut } from "lucide-react";
import { useAuth, useTheme, useEvents, useLiveRound, browserPushPermission, requestBrowserPush } from "../lib/store";
import { api } from "../lib/api";
import { toast } from "sonner";

// GLCC top header.
// Left: cog icon → Settings popover (notification toggles, display picker,
// Exit). Right: mail (DMs), bell (notification feed). A centered "GLCC."
// wordmark sits in the true middle of the bar, colored per the active tab
// (same tokens the bottom tab bar already uses for its active icon — see
// WORDMARK_CLASS below). Both popovers use the same pink 1px border
// language established in the Coffee tab.
const WORDMARK_CLASS = {
  rides: "text-accent-strava",
  coffee: "text-accent-pink",
  riders: "text-status-cant",
  chat: "text-[#007AFF]",
};

export default function Header({ onOpenDM, dmUnread, notifPrefs, onPrefsChange, onNavigate, activeTab }) {
  const { user, logout } = useAuth();
  const { open: openLiveRound } = useLiveRound();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [feed, setFeed] = useState({ items: [], unread: 0 });
  const { subscribe } = useEvents();

  // Refresh the bell feed on mount + on any live club event that would
  // add to it. Debounced trivially by only re-fetching when the popover
  // gets opened, plus a background refresh every 60s.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setFeed(data);
      } catch (_) { /* ignore */ }
    };
    refresh();
    const id = setInterval(refresh, 60000);
    const unsub = subscribe((evt) => {
      if (["chat.mechanical", "chat.mechanical.resolved", "coffee.round.started", "chat.mention"].includes(evt.type)) {
        refresh();
      }
    });
    return () => { cancelled = true; clearInterval(id); unsub && unsub(); };
  }, [user, subscribe]);

  async function openBell() {
    setBellOpen((v) => !v);
    if (!bellOpen) {
      try { await api.post("/notifications/read"); } catch (_) { /* ignore */ }
      setFeed((f) => ({ ...f, unread: 0 }));
    }
  }

  async function clearFeed() {
    try {
      await api.post("/notifications/clear");
      setFeed({ items: [], unread: 0 });
    } catch (_) { toast.error("Couldn't clear notifications"); }
  }

  // Route to the corresponding tab (and force-open the live coffee splash
  // when it's a coffee ping so the rider lands right on the tally).
  async function onFeedItemTap(item) {
    setBellOpen(false);
    if (item.kind === "coffee") {
      onNavigate?.("coffee");
      try {
        const { data } = await api.get("/coffee/rounds/active");
        const first = (data.rounds || [])[0];
        if (first) openLiveRound(first);
      } catch (_) { /* ignore */ }
      return;
    }
    if (item.kind === "mechanical" || item.kind === "mention") {
      onNavigate?.("chat");
      return;
    }
    if (item.kind === "rider") {
      onNavigate?.("riders");
    }
  }

  return (
    <div className="relative flex items-center justify-between px-4 pt-3 pb-3 sm:pt-8 border-b border-border-subtle">
      {/* Left — cog. Exit now lives inside its dropdown, not as a top-level
          element. */}
      <button
        onClick={() => { setSettingsOpen((v) => !v); setBellOpen(false); }}
        className={`p-1.5 rounded-full transition ${settingsOpen ? "text-accent-pink" : "text-text-secondary hover:text-brand-accent"}`}
        title="Settings"
        aria-label="Settings"
        data-testid="header-cog"
      >
        <Settings className="w-7 h-7" />
      </button>

      {/* Centered wordmark — absolutely positioned against the whole bar
          (not just flexed between the two icon groups) so it's truly
          centered regardless of how wide the left/right groups are.
          Colored per the active tab, reusing the exact same tokens the
          bottom tab bar already uses for its active icon. */}
      <div
        className={`absolute left-1/2 bottom-3 font-heading uppercase leading-none pointer-events-none select-none ${WORDMARK_CLASS[activeTab] || "text-brand-accent"}`}
        style={{ fontSize: "34px", fontWeight: 900, letterSpacing: "0.06em", WebkitFontSmoothing: "antialiased", textRendering: "geometricPrecision", transform: "translateX(-50%) translateZ(0)", marginBottom: "3px" }}
        data-testid="header-wordmark"
      >
        GLCC
      </div>

      {/* Right — mail · bell */}
      <div className="flex items-center gap-3">
        <button
          onClick={onOpenDM}
          className="relative p-1.5 rounded-full text-text-secondary hover:text-brand-accent transition"
          title="Messages"
          aria-label="Messages"
          data-testid="dm-open"
        >
          <Mail className="w-[26px] h-[26px]" />
          {dmUnread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-accent-pink text-white text-[10px] font-black flex items-center justify-center leading-none"
              data-testid="dm-badge"
            >
              {dmUnread > 9 ? "9+" : dmUnread}
            </span>
          )}
        </button>
        <button
          onClick={() => { openBell(); setSettingsOpen(false); }}
          className={`relative p-1.5 rounded-full transition ${bellOpen || feed.unread > 0 ? "text-accent-pink" : "text-text-secondary hover:text-brand-accent"}`}
          title="Notifications"
          aria-label="Notifications"
          data-testid="header-bell"
        >
          <Bell className="w-[26px] h-[26px]" />
          {feed.unread > 0 && !bellOpen && (
            <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-accent-pink ring-2 ring-bg-primary" />
          )}
        </button>
      </div>

      {settingsOpen && (
        <SettingsPopover
          notifPrefs={notifPrefs}
          onPrefsChange={onPrefsChange}
          onLogout={logout}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {bellOpen && (
        <NotificationsPopover
          items={feed.items}
          onClose={() => setBellOpen(false)}
          onTap={onFeedItemTap}
          onClear={clearFeed}
        />
      )}
    </div>
  );
}

function SettingsPopover({ notifPrefs, onPrefsChange, onClose, onLogout }) {
  const { theme, cycleTheme, setTheme } = useTheme();
  const ref = useRef(null);
  useEffect(() => {
    // Ignore taps on the cog button itself — its onClick already toggles
    // the popover, so treating this click as "outside" would close-then-
    // reopen and cog-tap would appear to do nothing.
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-testid="header-cog"]')) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose]);

  const toggle = async (key) => {
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    onPrefsChange(next);  // optimistic
    try {
      const { data } = await api.put("/users/me/notification-prefs", { [key]: next[key] });
      onPrefsChange(data.notification_prefs || next);
      // If they turn Coffee/Chat/DM on but browser perm isn't granted, offer it.
      if (next[key] && browserPushPermission() === "default") {
        requestBrowserPush().catch(() => {});
      }
    } catch (_) {
      onPrefsChange(notifPrefs); // rollback
      toast.error("Couldn't save");
    }
  };

  const rows = [
    { key: "mechanical", label: "Mechanical alerts", sub: "Recommended", icon: Wrench },
    { key: "coffee", label: "Coffee rounds", sub: null, icon: Coffee },
    { key: "chat", label: "Club chat", sub: null, icon: MessageCircle },
    { key: "dm", label: "Private messages", sub: null, icon: Mail },
  ];

  return (
    <div
      ref={ref}
      className="absolute top-14 left-3 w-[320px] max-w-[calc(100vw-24px)] z-50 bg-bg-secondary border border-accent-pink rounded-2xl shadow-2xl p-4 animate-in fade-in zoom-in-95 duration-150"
      data-testid="settings-popover"
    >
      <div className="absolute -top-1.5 left-4 w-3 h-3 bg-bg-secondary border-l border-t border-accent-pink rotate-45" />
      <div className="text-[11px] font-mono-stat uppercase tracking-widest text-accent-pink mb-3 px-1">Settings</div>
      <div className="rounded-xl bg-bg-primary border border-border-subtle overflow-hidden">
        {rows.map((r, i) => {
          const Icon = r.icon;
          const on = !!notifPrefs?.[r.key];
          return (
            <button
              key={r.key}
              onClick={() => toggle(r.key)}
              className={`w-full flex items-center gap-3 px-3.5 py-3 text-left ${i > 0 ? "border-t border-border-subtle" : ""}`}
              data-testid={`pref-${r.key}`}
            >
              <Icon className="w-4 h-4 text-text-muted shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-text-primary truncate">{r.label}</div>
                {r.sub && <div className="text-[10px] font-mono-stat text-text-muted uppercase tracking-widest mt-0.5">{r.sub}</div>}
              </div>
              <div className={`relative w-11 h-6 rounded-full transition ${on ? "bg-accent-pink" : "bg-bg-secondary border border-border-subtle"}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] font-mono-stat uppercase tracking-widest text-text-muted mt-4 mb-2 px-1">Display</div>
      <div className="flex bg-bg-primary rounded-xl p-1 gap-1">
        {["auto", "dark", "light"].map((opt) => (
          <button
            key={opt}
            onClick={() => setTheme(opt)}
            className={`flex-1 text-[13px] font-semibold capitalize py-2 rounded-lg ${theme === opt ? "bg-accent-pink text-white" : "text-text-secondary"}`}
            data-testid={`theme-${opt}`}
          >
            {opt}
          </button>
        ))}
      </div>
      <button
        onClick={onLogout}
        className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-widest text-status-cant hover:opacity-80 border-t border-border-subtle pt-3"
        data-testid="header-exit"
      >
        <LogOut className="w-4 h-4" /> Exit
      </button>
    </div>
  );
}

function NotificationsPopover({ items, onClose, onTap, onClear }) {
  const ref = useRef(null);
  useEffect(() => {
    // Same story as the settings popover — tapping the bell should close
    // via its own onClick, not via the outside-click listener.
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-testid="header-bell"]')) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [onClose]);

  const iconFor = (kind) => {
    if (kind === "mechanical") return { icon: Wrench, cls: "bg-status-cant/15 text-status-cant" };
    if (kind === "coffee") return { icon: Coffee, cls: "bg-accent-pink/15 text-accent-pink" };
    if (kind === "rider") return { icon: Users, cls: "bg-brand-accent/15 text-brand-accent" };
    return { icon: MessageCircle, cls: "bg-[#007AFF]/15 text-[#007AFF]" };
  };

  return (
    <div
      ref={ref}
      className="absolute top-14 right-4 w-[340px] max-w-[calc(100vw-32px)] z-50 bg-bg-secondary border border-accent-pink rounded-2xl shadow-2xl p-3 animate-in fade-in zoom-in-95 duration-150"
      data-testid="notifications-popover"
    >
      <div className="absolute -top-1.5 right-4 w-3 h-3 bg-bg-secondary border-l border-t border-accent-pink rotate-45" />
      <div className="flex items-center justify-between px-2 py-1">
        <div className="text-[10px] font-mono-stat uppercase tracking-widest text-accent-pink">Notifications</div>
        {items.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="flex items-center gap-1 text-[9px] font-mono-stat uppercase tracking-widest text-text-muted hover:text-status-cant"
            data-testid="notif-clear"
          >
            <Trash2 className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
      <div className="max-h-[360px] overflow-y-auto no-scrollbar">
        {items.length === 0 && (
          <div className="px-3 py-6 text-center text-[11px] text-text-muted font-mono-stat uppercase tracking-widest">All quiet</div>
        )}
        {items.map((it) => {
          const { icon: Icon, cls } = iconFor(it.kind);
          return (
            <button
              type="button"
              key={it.id}
              onClick={() => onTap && onTap(it)}
              className="relative w-full flex gap-2.5 px-2 py-2 border-b border-border-subtle last:border-b-0 text-left hover:bg-bg-primary/60 transition"
              data-testid={`notif-item-${it.kind}`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${cls}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12.5px] text-text-primary leading-tight">
                  <b className="font-semibold">{it.title}</b> {it.subtitle}
                </div>
                <div className="text-[10px] text-text-muted font-mono-stat mt-0.5">{fmtRelative(it.created_at)}</div>
              </div>
              {it.unread && <div className="absolute right-2 top-3 w-1.5 h-1.5 rounded-full bg-accent-pink" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
